import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Eye, Search, Filter, User, Mail, Phone, MapPin, Home, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { 
  getUsers, 
  createUser, 
  updateUser, 
  deleteUser,
  createUserAddress,
  updateUserAddress,
  deleteUserAddress,
  getUserAddresses
} from '../lib/pocketbase';
import { User as UserType, UserAddress, UserForm, UserAddressForm } from '../types';
import { formatPersianDate } from '../lib/utils';
import { Table, TableHeader, TableRow, TableCell, TableHeaderCell } from '../components/ui/Table';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Textarea from '../components/ui/Textarea';
import Button from '../components/ui/Button';
import FileUpload from '../components/ui/FileUpload';

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [editingAddress, setEditingAddress] = useState<UserAddress | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [apiSearchTerm, setApiSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'basic' | 'addresses'>('basic');

  const [userFormData, setUserFormData] = useState<UserForm>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    nationalCode: '',
    sex: undefined,
    is_online: false
  });

  const [addressFormData, setAddressFormData] = useState<UserAddressForm>({
    title: '',
    address: '',
    postalCode: ''
  });

  const [userFormFiles, setUserFormFiles] = useState<{
    avatar: File[];
  }>({
    avatar: []
  });

  const [errors, setErrors] = useState<Partial<UserForm>>({});

  const fetchUsers = useCallback(async () => {
    try {
      const response = await getUsers(currentPage, perPage, apiSearchTerm);
      setUsers(response.items);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      if (error?.message?.includes('autocancelled')) {
        return;
      }
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, apiSearchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setApiSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      
      if (!editingUser) {
        // Only add email and password for new users
        formData.append('email', userFormData.email);
        if (userFormData.password) {
          formData.append('password', userFormData.password);
        }
      }
      
      formData.append('first_name', userFormData.first_name || '');
      formData.append('last_name', userFormData.last_name || '');
      formData.append('phone', userFormData.phone || '');
      formData.append('nationalCode', userFormData.nationalCode || '');
      if (userFormData.sex) {
        formData.append('sex', userFormData.sex);
      }
      formData.append('is_online', userFormData.is_online ? 'true' : 'false');

      if (userFormFiles.avatar.length > 0) {
        formData.append('avatar', userFormFiles.avatar[0]);
      }

      if (editingUser) {
        await updateUser(editingUser.id, formData);
      } else {
        await createUser(formData);
      }
      
      setShowUserModal(false);
      setEditingUser(null);
      resetUserForm();
      fetchUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      const data = {
        title: addressFormData.title || '',
        address: addressFormData.address || '',
        postalCode: addressFormData.postalCode || '',
        user_id: editingUser.id
      };

      if (editingAddress) {
        await updateUserAddress(editingAddress.id, data);
      } else {
        await createUserAddress(data);
      }
      
      // Refresh addresses for the current user
      const addresses = await getUserAddresses(editingUser.id);
      setEditingUser({
        ...editingUser,
        expand: {
          users_address: addresses
        }
      });
      
      setEditingAddress(null);
      resetAddressForm();
    } catch (error: any) {
      console.error('Error saving address:', error);
    }
  };

  const handleEditUser = async (user: UserType) => {
    setEditingUser(user);
    setUserFormData({
      email: user.email || '',
      password: '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      nationalCode: user.nationalCode || '',
      sex: user.sex || undefined,
      is_online: user.is_online || false
    });
    setUserFormFiles({ avatar: [] });
    setActiveTab('basic');
    
    // Fetch addresses for this user
    try {
      const addresses = await getUserAddresses(user.id);
      setEditingUser({
        ...user,
        expand: {
          users_address: addresses
        }
      });
    } catch (error: any) {
      console.error('Error fetching addresses:', error);
      setEditingUser({
        ...user,
        expand: {
          users_address: []
        }
      });
    }
    
    setShowUserModal(true);
  };

  const handleEditAddress = (address: UserAddress) => {
    setEditingAddress(address);
    setAddressFormData({
      title: address.title || '',
      address: address.address || '',
      postalCode: address.postalCode || ''
    });
  };

  const handleDeleteUser = async (id: string) => {
    if (window.confirm('آیا از حذف این کاربر اطمینان دارید؟')) {
      try {
        await deleteUser(id);
        fetchUsers();
      } catch (error: any) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleDeleteAddress = async (id: string) => {
    if (window.confirm('آیا از حذف این آدرس اطمینان دارید؟')) {
      try {
        await deleteUserAddress(id);
        
        // Refresh addresses for the current user
        if (editingUser) {
          const addresses = await getUserAddresses(editingUser.id);
          setEditingUser({
            ...editingUser,
            expand: {
              users_address: addresses
            }
          });
        }
      } catch (error: any) {
        console.error('Error deleting address:', error);
      }
    }
  };

  const resetUserForm = () => {
    setUserFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      nationalCode: '',
      sex: undefined,
      is_online: false
    });
    setUserFormFiles({ avatar: [] });
    setErrors({});
  };

  const resetAddressForm = () => {
    setAddressFormData({
      title: '',
      address: '',
      postalCode: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">مدیریت کاربران</h1>
          <p className="text-gray-600">مشاهده و مدیریت تمام کاربران و آدرس‌های آن‌ها</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">جستجو</label>
            <Input
              type="text"
              placeholder="جستجو در کاربران..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <Table>
          <TableHeader>
            <TableHeaderCell>کاربر</TableHeaderCell>
            <TableHeaderCell>کد ملی</TableHeaderCell>
            <TableHeaderCell>جنسیت</TableHeaderCell>
            <TableHeaderCell>شماره تلفن</TableHeaderCell>
            <TableHeaderCell>وضعیت</TableHeaderCell>
            <TableHeaderCell>تاریخ عضویت</TableHeaderCell>
            <TableHeaderCell>عملیات</TableHeaderCell>
          </TableHeader>
          <tbody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      {user.avatar ? (
                        <img 
                          src={`${process.env.REACT_APP_POCKETBASE_URL}/api/files/users/${user.id}/${user.avatar}`} 
                          alt={`${user.first_name} ${user.last_name}`}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-5 w-5 text-primary-600" />
                      )}
                    </div>
                    <div className="mr-3">
                      <p className="font-medium text-gray-900">
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name}`
                          : user.first_name || user.last_name || 'بدون نام'
                        }
                      </p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-900">
                    {user.nationalCode || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-900">
                    {user.sex === 'man' ? 'مرد' : user.sex === 'woman' ? 'زن' : '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 text-gray-400 ml-1" />
                    <span className="text-sm text-gray-900">
                      {user.phone || '-'}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full ml-1 ${
                      user.is_online ? 'bg-green-500' : 'bg-gray-400'
                    }`}></div>
                    <span className="text-sm text-gray-600">
                      {user.is_online ? 'آنلاین' : 'آفلاین'}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 ml-1" />
                    <span className="text-sm text-gray-900">{formatPersianDate(user.created)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center overflow-x-auto">
          <nav className="flex items-center space-x-2 space-x-reverse">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-secondary disabled:opacity-50"
            >
              قبلی
            </button>
            
            {(() => {
              const pages = [];
              const maxVisiblePages = 7; // Show max 7 page buttons
              
              if (totalPages <= maxVisiblePages) {
                // Show all pages if total is small
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(i);
                }
              } else {
                // Show first 3 pages
                for (let i = 1; i <= 3; i++) {
                  pages.push(i);
                }
                
                // Add ellipsis if current page is not near the beginning
                if (currentPage > 4) {
                  pages.push('...');
                }
                
                // Show current page and neighbors
                const start = Math.max(4, currentPage - 1);
                const end = Math.min(totalPages - 3, currentPage + 1);
                
                for (let i = start; i <= end; i++) {
                  if (!pages.includes(i)) {
                    pages.push(i);
                  }
                }
                
                // Add ellipsis if current page is not near the end
                if (currentPage < totalPages - 3) {
                  pages.push('...');
                }
                
                // Show last 3 pages
                for (let i = totalPages - 2; i <= totalPages; i++) {
                  if (!pages.includes(i)) {
                    pages.push(i);
                  }
                }
              }
              
              return pages.map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === 'number' && setCurrentPage(page)}
                  disabled={page === '...'}
                  className={`px-3 py-2 text-sm font-medium rounded-lg ${
                    page === currentPage
                      ? 'bg-primary-600 text-white'
                      : page === '...'
                      ? 'bg-white text-gray-400 cursor-default'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ));
            })()}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn-secondary disabled:opacity-50"
            >
              بعدی
            </button>
          </nav>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-[calc(100%-2rem)] max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingUser ? 'ویرایش کاربر' : 'کاربر جدید'}
              </h3>
              
              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8 space-x-reverse">
                  <button
                    onClick={() => setActiveTab('basic')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'basic'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    اطلاعات پایه
                  </button>
                  <button
                    onClick={() => setActiveTab('addresses')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'addresses'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    آدرس‌ها
                  </button>
                </nav>
              </div>

              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <form onSubmit={handleUserSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {!editingUser && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">ایمیل</label>
                          <Input
                            type="email"
                            value={userFormData.email}
                            onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                            placeholder="ایمیل کاربر"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">رمز عبور</label>
                          <Input
                            type="password"
                            value={userFormData.password}
                            onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                            placeholder="رمز عبور"
                            required
                          />
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">نام</label>
                      <Input
                        type="text"
                        value={userFormData.first_name}
                        onChange={(e) => setUserFormData({ ...userFormData, first_name: e.target.value })}
                        placeholder="نام کاربر"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">نام خانوادگی</label>
                      <Input
                        type="text"
                        value={userFormData.last_name}
                        onChange={(e) => setUserFormData({ ...userFormData, last_name: e.target.value })}
                        placeholder="نام خانوادگی کاربر"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">شماره تلفن</label>
                      <Input
                        type="text"
                        value={userFormData.phone}
                        onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                        placeholder="شماره تلفن"
                        readOnly={!!editingUser}
                        className={editingUser ? "bg-gray-100 cursor-not-allowed" : ""}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">کد ملی</label>
                      <Input
                        type="text"
                        value={userFormData.nationalCode}
                        onChange={(e) => setUserFormData({ ...userFormData, nationalCode: e.target.value })}
                        placeholder="کد ملی"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">جنسیت</label>
                      <Select
                        value={userFormData.sex || ''}
                        onChange={(e) => setUserFormData({ ...userFormData, sex: e.target.value as 'man' | 'woman' | undefined })}
                        options={[
                          { value: '', label: 'انتخاب کنید' },
                          { value: 'man', label: 'مرد' },
                          { value: 'woman', label: 'زن' }
                        ]}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_online"
                        checked={userFormData.is_online}
                        onChange={(e) => setUserFormData({ ...userFormData, is_online: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_online" className="mr-2 block text-sm text-gray-900">
                        آنلاین
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">تصویر پروفایل</label>
                    <FileUpload
                      value={userFormFiles.avatar}
                      onChange={(files) => setUserFormFiles({ ...userFormFiles, avatar: files })}
                      accept="image/*"
                      maxFiles={1}
                    />
                  </div>
                  <div className="flex justify-end space-x-3 space-x-reverse">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowUserModal(false);
                        setEditingUser(null);
                        resetUserForm();
                      }}
                    >
                      انصراف
                    </Button>
                    <Button type="submit" disabled={loading}>
                      {loading ? 'در حال ذخیره...' : (editingUser ? 'ویرایش' : 'ایجاد')}
                    </Button>
                  </div>
                </form>
              )}

              {/* Addresses Tab */}
              {activeTab === 'addresses' && editingUser && (
                <div className="space-y-4">
                  {/* Existing Addresses */}
                  {editingUser.expand?.users_address && editingUser.expand.users_address.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-md font-medium text-gray-900 mb-3">آدرس‌های موجود</h4>
                      <div className="space-y-2">
                        {editingUser.expand.users_address.map((address) => (
                          <div key={address.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">{address.title || 'بدون عنوان'}</p>
                              <p className="text-sm text-gray-600">{address.address}</p>
                              {address.postalCode && (
                                <p className="text-sm text-gray-500">کد پستی: {address.postalCode}</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 space-x-reverse">
                              <button
                                onClick={() => handleEditAddress(address)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteAddress(address.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add New Address Form */}
                  <div className="border-t pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-3">
                      {editingAddress ? 'ویرایش آدرس' : 'افزودن آدرس جدید'}
                    </h4>
                    <form onSubmit={handleAddressSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">عنوان آدرس</label>
                        <Input
                          type="text"
                          value={addressFormData.title}
                          onChange={(e) => setAddressFormData({ ...addressFormData, title: e.target.value })}
                          placeholder="مثل: خانه، محل کار"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">آدرس کامل</label>
                        <Textarea
                          value={addressFormData.address}
                          onChange={(e) => setAddressFormData({ ...addressFormData, address: e.target.value })}
                          placeholder="آدرس کامل"
                          rows={3}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">کد پستی</label>
                        <Input
                          type="text"
                          value={addressFormData.postalCode}
                          onChange={(e) => setAddressFormData({ ...addressFormData, postalCode: e.target.value })}
                          placeholder="کد پستی"
                        />
                      </div>
                      <div className="flex justify-end space-x-3 space-x-reverse">
                        {editingAddress && (
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => {
                              setEditingAddress(null);
                              resetAddressForm();
                            }}
                          >
                            انصراف
                          </Button>
                        )}
                        <Button type="submit">
                          {editingAddress ? 'ویرایش' : 'افزودن'}
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage; 